import {
  IWorkflowStep,
  SitePermitStatus,
  WorkflowStepType,
} from '~/client/graph'
import formStatusesByStepsMap from '~/client/src/shared/constants/formStatusesByStepsMap'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

export interface IStatusItem {
  status: SitePermitStatus
  workflowStepLevel: number
}

function getStepTypeCounts(type: WorkflowType): Map<WorkflowStepType, number> {
  const counts = new Map<WorkflowStepType, number>()
  const steps = type.stepsWithoutRecurring
  steps.forEach(step => {
    counts.set(step.type, (counts.get(step.type) || 0) + 1)
  })
  return counts
}

function getStepItemsForType(type: WorkflowType): IStatusItem[] {
  const steps = type.stepsWithoutRecurring
  const typeCounts = getStepTypeCounts(type)
  const seenCounts = new Map<WorkflowStepType, number>()
  const items: IStatusItem[] = []

  steps.forEach((step, idx) => {
    const isLast = idx === steps.length - 1
    const statuses = isLast
      ? step.type === WorkflowStepType.Inspection
        ? [SitePermitStatus.Failed, SitePermitStatus.Done]
        : [SitePermitStatus.Done]
      : (formStatusesByStepsMap[step.type] || []).filter(
          s => s !== SitePermitStatus.Changed,
        )

    const totalOfType = typeCounts.get(step.type) || 0
    const seen = seenCounts.get(step.type) || 0
    seenCounts.set(step.type, seen + 1)

    const level = isLast ? 0 : totalOfType <= 1 ? 0 : seen + 1

    statuses.forEach(status => {
      items.push({ status, workflowStepLevel: level })
    })
  })

  return items
}

export function getCommonStepItems(
  permits: Workflow[],
  workflowTypesStore: WorkflowTypesStore,
): IStatusItem[] {
  const types = permits
    .map(p => workflowTypesStore.getPermitTypeById(p.typeId))
    .filter(Boolean)
  if (!types.length) return []

  const allItemSets = types.map(t => getStepItemsForType(t))

  // Build a set of (status, level) keys present in each template
  const keySets = allItemSets.map(
    items => new Set(items.map(i => `${i.status}:${i.workflowStepLevel}`)),
  )

  // Use the first template's items as the canonical order
  const [firstKeys, ...restKeys] = keySets
  const commonKeys = new Set(
    Array.from(firstKeys).filter(k => restKeys.every(set => set.has(k))),
  )

  return allItemSets[0].filter(i =>
    commonKeys.has(`${i.status}:${i.workflowStepLevel}`),
  )
}

/**
 * Resolves the workflow step in a template that matches a given status and level.
 * Used during bulk save to find the correct step ID per-permit.
 */
export function resolveStepForStatus(
  status: SitePermitStatus,
  level: number,
  type: WorkflowType,
): IWorkflowStep | undefined {
  const steps = type.stepsWithoutRecurring

  if (status === SitePermitStatus.Done) {
    return steps[steps.length - 1]
  }

  if (status === SitePermitStatus.Failed && level === 0) {
    const lastStep = steps[steps.length - 1]
    if (lastStep?.type === WorkflowStepType.Inspection) return lastStep
  }

  // Reverse map: find which step types produce this status
  const targetTypes: WorkflowStepType[] = []
  for (const [stepType, statuses] of Object.entries(formStatusesByStepsMap)) {
    if (statuses.includes(status)) {
      targetTypes.push(stepType as WorkflowStepType)
    }
  }

  const matchingSteps = steps.filter(
    (s, idx) => targetTypes.includes(s.type) && idx !== steps.length - 1,
  )

  if (level === 0) {
    return matchingSteps[0]
  }
  return matchingSteps[level - 1]
}
